import React, { useState, useEffect, useMemo, useRef } from "react";
import { v4 } from "uuid";
import { useTranslation } from "react-i18next";
import { DataTable, DataTableFilterMeta } from "primereact/datatable";
import { Column } from "primereact/column";
import { FilterMatchMode } from "primereact/api";
import { Dropdown } from "primereact/dropdown";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { User, UserStatus } from "../../models/User";
import { NewUserForm } from "../../forms/NewUserForm/NewUserForm";
import { getAllUsers, getOneUser } from "../../services/UserService";
import "./UsersPage.scss";

type UserRow = User & {
  cityName?: string;
  roleName?: string;
  warehouseName?: string;
};

export const UsersPage = () => {
  const { t } = useTranslation();
  const toast = useRef<Toast>(null);

  const [processing, setProcessing] = useState(false);
  const [allUsers, setAllUsers] = useState<UserRow[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | undefined>();
  const [showForm, setShowForm] = useState<string>("");

  const [filters, setFilters] = useState<DataTableFilterMeta>({
    firstName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    lastName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    address: { value: null, matchMode: FilterMatchMode.CONTAINS },
    cityName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    nationalIdNumber: { value: null, matchMode: FilterMatchMode.CONTAINS },
    personalCodeNumber: { value: null, matchMode: FilterMatchMode.CONTAINS },
    email: { value: null, matchMode: FilterMatchMode.CONTAINS },
    roleName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    warehouseName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    status: { value: null, matchMode: FilterMatchMode.EQUALS },
  });

  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);

  const refreshUsers = async () => {
    setProcessing(true);
    try {
      const resp = await getAllUsers();

      const list: User[] = resp?.data?.result ?? resp?.data ?? [];

      const normalized: UserRow[] = list.map((u: any) => ({
        ...u,
        cityName: u?.city?.name ?? "",
        roleName: u?.role?.name ?? "",
        warehouseName: u?.warehouse?.name ?? "",
      }));

      setAllUsers(normalized);
    } catch (e) {
      toast.current?.show({
        severity: "error",
        detail: String(t("Greška pri učitavanju korisnika")),
        life: 3000,
      });
    } finally {
      setProcessing(false);
    }
  };

  useEffect(() => {
    refreshUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tableValue = useMemo(() => allUsers, [allUsers]);

  const statusOptions = useMemo(
    () => [
      { label: t("Sva stanja"), value: null },
      { label: t("Aktivan"), value: UserStatus.Active },
      { label: t("Blokiran"), value: UserStatus.Blocked },
    ],
    [t]
  );

  const statusFilterElement = (options: any) => {
    return (
      <Dropdown
        value={options.value}
        options={statusOptions}
        onChange={(e) => options.filterApplyCallback(e.value)}
        placeholder={String(t("Izaberite"))}
        className="p-column-filter"
        showClear={false}
      />
    );
  };

  const cityBody = (row: UserRow) => <span>{row?.city?.name ?? ""}</span>;

  const roleBody = (row: UserRow) => <span>{row?.role?.name ?? ""}</span>;

  const warehouseBody = (row: UserRow) => (
    <span>{row?.warehouse?.name ?? ""}</span>
  );

  const statusBody = (row: UserRow) => {
    const isActive = row.status === UserStatus.Active;
    return (
      <div className="action-button">
        <ButtonInput
          addStyle={{
            border: "none",
            background: isActive ? "#198754" : "#DC3545",
          }}
          iconAlign="right"
          icon="bi bi-exclamation-octagon"
          labelText={isActive ? t("Aktivan") : t("Blokiran")}
          onButtonClick={() => {}}
        />
      </div>
    );
  };

  const actionsBody = (row: UserRow) => {
    return (
      <div className="action-button">
        <ButtonInput
          addStyle={{ border: "none", background: "#198754" }}
          labelText={""}
          icon="bi bi-pencil"
          onButtonClick={async () => {
            try {
              const resp = await getOneUser(row.id as string);
              const user: User = resp?.data?.result ?? resp?.data;
              setSelectedUser(user);
              setShowForm(v4());
            } catch (e) {
              toast.current?.show({
                severity: "error",
                detail: String(t("Greška pri učitavanju korisnika")),
                life: 3000,
              });
            }
          }}
        />
      </div>
    );
  };

  return (
    <PageLayout>
      <Toast ref={toast} position="top-right" />
      <div className="table-wrapper">
        <div className="table-header">
          <div className="header-left">
            <i
              className="bi bi-people header-title-icon"
              style={{
                fontSize: 14,
                marginLeft: "1rem",
              }}
            />
            <span className="header-left-title">
              {t("Lista svih korisnika sistema")}
            </span>
          </div>
          <div className="header-right">
            <NewUserForm
              show={showForm}
              user={selectedUser}
              buttonLabel={String(t("Dodaj korisnika"))}
              buttonIcon="bi bi-person-plus"
              buttonStyle={{
                border: "none",
                background: "#ffffff",
                color: "#6C757D",
                marginRight: "1rem",
                margin: "0 1rem 0 0",
              }}
              refreshingData={refreshUsers}
            />
          </div>
        </div>
        {processing ? (
          <ProgressSpinner />
        ) : (
          <div className="table-body">
            <DataTable
              value={tableValue}
              dataKey="id"
              scrollable
              scrollHeight="80vh"
              stripedRows
              showGridlines
              removableSort
              sortMode="single"
              filterDisplay="row"
              filters={filters}
              onFilter={(e) => {
                setFilters(e.filters as DataTableFilterMeta);
                setFirst(0);
              }}
              paginator
              first={first}
              rows={rows}
              onPage={(e) => {
                setFirst(e.first);
                setRows(e.rows);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
              currentPageReportTemplate=""
              emptyMessage={t("Nema korisnika za zadate kriterijume")}
            >
              <Column
                field="firstName"
                header={t("Ime")}
                sortable
                filter
                style={{ minWidth: "180px" }}
              />
              <Column
                field="lastName"
                header={t("Prezime")}
                sortable
                filter
                style={{ minWidth: "200px" }}
              />
              <Column
                field="address"
                header={t("Adresa")}
                sortable
                filter
                style={{ minWidth: "240px" }}
              />
              <Column
                field="cityName"
                header={t("Mesto/Naselje")}
                body={cityBody}
                sortable
                filter
                style={{ minWidth: "220px" }}
              />
              <Column
                field="nationalIdNumber"
                header={t("Br.Lične karte")}
                sortable
                filter
                style={{ minWidth: "180px" }}
              />
              <Column
                field="personalCodeNumber"
                header={t("JMBG")}
                sortable
                filter
                style={{ minWidth: "180px" }}
              />
              <Column
                field="email"
                header={t("Email")}
                sortable
                filter
                style={{ minWidth: "240px" }}
              />
              <Column
                field="roleName"
                header={t("Uloga")}
                body={roleBody}
                sortable
                filter
                style={{ minWidth: "220px" }}
              />
              <Column
                field="warehouseName"
                header={t("Magacin")}
                body={warehouseBody}
                sortable
                filter
                style={{ minWidth: "220px" }}
              />
              <Column
                field="status"
                header={t("Status")}
                body={statusBody}
                filter
                filterElement={statusFilterElement}
                showFilterMenu={false}
                style={{ minWidth: "170px" }}
              />
              <Column
                header={t("Opcije")}
                body={actionsBody}
                style={{ minWidth: "120px" }}
              />
            </DataTable>
          </div>
        )}
      </div>
    </PageLayout>
  );
};
